// @flow
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import axios from 'axios';
import { defaultReducers } from './defaultReducers';
import { participantSliceActions } from './participantSlice';
import { coachSliceActions } from './coachesSlice';
import { getToday } from '../utilities/getToday';
import {
  boardFlightKey,
  positiveId,
  shouldApplyBoardResult,
  singleFlight,
} from '../schedule/realtime/boardIdentity';

export interface ScheduleType {
  id: ?number;
  type: string;
}

export interface ScheduleState {
  id: ?number;
  loading: boolean;
  error: string;
  isOutdated: boolean;
  type: ScheduleType;
  types: ScheduleType[];
  date: string;
  program: number;
  programName: ?string;
  // Live schedule socket (see docs/features/live-schedule-socket.md)
  socketStatus: string;
  presenceUsers: Array<{
    userId: number,
    firstName?: string,
    lastName?: string,
  }>;
  locksByParticipantUserId: { [key: string]: number };
  modifiedDate: ?string;
  boardEpoch: number;
  intendedBoard: ?{
    programId: ?number,
    date: string,
    scheduleTypeId: ?number,
    isMaster: boolean,
  };
}

const initialState: ScheduleState = {
  id: undefined,
  loading: false,
  error: '',
  isOutdated: false,
  type: {
    id: undefined,
    type: '',
  },
  types: [],
  date: getToday(),
  program: 0,
  programName: null,
  socketStatus: 'closed',
  presenceUsers: [],
  locksByParticipantUserId: {},
  modifiedDate: null,
  boardEpoch: 0,
  intendedBoard: null,
};

const schedule = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    checkOutdated: (state, action: PayloadAction<any>) => {
      // When the live socket is open, deltas own sync — never show the old
      // "outdated please refresh" banner (that defeats the live feed).
      if (state.socketStatus === 'open') {
        if (action.payload.modifiedDate) {
          state.modifiedDate = action.payload.modifiedDate;
        }
        state.isOutdated = false;
        return;
      }
      if (
        action.payload.modifiedDate != null &&
        action.payload.modifiedDate !== state.modifiedDate
      ) {
        state.isOutdated = true;
      }
      if (action.payload.modifiedDate != null) {
        state.modifiedDate = action.payload.modifiedDate;
      }
    },
    setSocketStatus: (state, action: PayloadAction<string>) => {
      state.socketStatus = action.payload;
    },
    setPresence: (state, action: PayloadAction<any>) => {
      state.presenceUsers = action.payload || [];
    },
    presenceJoin: (state, action: PayloadAction<any>) => {
      const user = action.payload;
      if (!user || user.userId == null) return;
      if (!state.presenceUsers.find((u) => u.userId === user.userId)) {
        state.presenceUsers.push(user);
      }
    },
    presenceLeave: (state, action: PayloadAction<any>) => {
      const user = action.payload;
      if (!user || user.userId == null) return;
      state.presenceUsers = state.presenceUsers.filter(
        (u) => u.userId !== user.userId
      );
    },
    setLock: (state, action: PayloadAction<any>) => {
      const { participantUserId, holderUserId } = action.payload || {};
      if (participantUserId == null) return;
      state.locksByParticipantUserId[String(participantUserId)] = holderUserId;
    },
    clearLock: (state, action: PayloadAction<any>) => {
      const participantUserId = action.payload;
      if (participantUserId == null) return;
      delete state.locksByParticipantUserId[String(participantUserId)];
    },
    clearRealtime: (state) => {
      state.socketStatus = 'closed';
      state.presenceUsers = [];
      state.locksByParticipantUserId = {};
    },
    /**
     * Patch schedule meta without toggling loading.
     * Socket/realtime must use this — getSuccess sets loading=false and was
     * blanking the spinner while schedule_by_date was still in flight.
     */
    patchMeta: (state, action: PayloadAction<any>) => {
      if (!action.payload) return;
      const {
        loading, // never allow accidental loading flips via meta
        ...rest
      } = action.payload;
      Object.assign(state, rest);
    },
    beginBoard: (state, action: PayloadAction<any>) => {
      const selection = action.payload || {};
      state.boardEpoch = (state.boardEpoch || 0) + 1;
      state.intendedBoard = {
        programId: positiveId(selection.programId),
        date: selection.date,
        scheduleTypeId: positiveId(selection.scheduleTypeId),
        isMaster: !!selection.isMaster,
      };
      state.id = null;
      state.modifiedDate = null;
      state.date = selection.date;
      state.loading = true;
      state.error = '';
    },
    adoptResolvedBoard: (state, action: PayloadAction<any>) => {
      if (!state.intendedBoard || !action.payload) {
        return;
      }
      const programId = positiveId(action.payload.programId);
      const scheduleTypeId = positiveId(action.payload.scheduleTypeId);
      if (programId != null) {
        state.intendedBoard.programId = programId;
      }
      if (scheduleTypeId != null) {
        state.intendedBoard.scheduleTypeId = scheduleTypeId;
      }
    },
    markBoardMismatch: (state) => {
      state.loading = false;
      state.error = 'Schedule view does not match the selected date.';
    },
    clearIntendedBoard: (state) => {
      state.intendedBoard = null;
    },
    getStart: defaultReducers.getStart,
    getSuccess: defaultReducers.getSuccess,
    getFailure: (state, action: PayloadAction<any>) => {
      defaultReducers.getFailure(state, action);
      // Also store the program that failed so we can display it in error messages
      if (action.payload.program !== undefined) {
        state.program = action.payload.program;
      }
      if (action.payload.programName !== undefined) {
        state.programName = action.payload.programName;
      }
    },
  },
});

export const scheduleSliceActions = schedule.actions;
export const scheduleReducer = schedule.reducer;

let activeScheduleCancel = null;

export function cancelActiveScheduleFetch() {
  if (activeScheduleCancel) {
    activeScheduleCancel.cancel('board changed');
    activeScheduleCancel = null;
  }
}

function readSchedule(getState) {
  return getState().schedule;
}

function failurePayload(err, program) {
  const errorMessage =
    (err.response &&
      err.response.data &&
      (err.response.data.message || err.response.data.error)) ||
    err.message ||
    'Failed to load schedule';
  const errorProgram =
    (err.response && err.response.data && err.response.data.program) || program;
  const errorProgramName =
    (err.response && err.response.data && err.response.data.programName) ||
    null;
  return {
    message: errorMessage,
    program: errorProgram,
    programName: errorProgramName,
  };
}

export const beginBoard = (selection: Object) => (dispatch: any) => {
  cancelActiveScheduleFetch();
  dispatch(scheduleSliceActions.beginBoard(selection));
  dispatch(participantSliceActions.normalizeList([]));
  dispatch(coachSliceActions.normalizeList([]));
};

export const fetchBasicSchedule = () => async (
  dispatch: any,
  getState: any
) => {
  const scheduleState = readSchedule(getState);
  const scheduleId = scheduleState.id;
  if (!scheduleId) {
    return null;
  }
  const epoch = scheduleState.intendedBoard ? scheduleState.boardEpoch : null;
  const capturedId = scheduleId;
  try {
    const schedule = await axios.get(
      `${
        process.env.REACT_APP_SERVER_URL || ''
      }/schedules/schedules/${scheduleId}/`
    );
    const now = readSchedule(getState);
    if (now.intendedBoard) {
      if (now.boardEpoch !== epoch || now.id !== capturedId) {
        return null;
      }
    }
    dispatch(scheduleSliceActions.checkOutdated(schedule.data));
  } catch (err) {
    if (axios.isCancel && axios.isCancel(err)) {
      return null;
    }
    const now = readSchedule(getState);
    if (now.intendedBoard && now.boardEpoch !== epoch) {
      return null;
    }
    dispatch(
      scheduleSliceActions.getFailure({
        message: failurePayload(err, now.program).message,
      })
    );
  }
};

function applyScheduleBody(dispatch, getState, schedule, captured, date) {
  const now = readSchedule(getState);
  if (!shouldApplyBoardResult(now, captured)) {
    return;
  }
  if (
    captured &&
    schedule.data.date != null &&
    String(schedule.data.date) !== String(captured.date)
  ) {
    return;
  }
  if (now.intendedBoard) {
    const typeId = schedule.data.scheduleType && schedule.data.scheduleType.id;
    dispatch(
      scheduleSliceActions.adoptResolvedBoard({
        programId: schedule.data.program,
        scheduleTypeId: typeId,
      })
    );
  }
  dispatch(
    participantSliceActions.normalizeList(
      schedule.data.scheduledParticipants || []
    )
  );
  dispatch(
    coachSliceActions.normalizeList(schedule.data.scheduledCoaches || [])
  );
  dispatch(
    scheduleSliceActions.getSuccess({
      id: schedule.data.id,
      isOutdated: false,
      date: captured ? captured.date : date,
      modifiedDate: schedule.data.modifiedDate,
      program: schedule.data.program,
      type: schedule.data.scheduleType,
      types: schedule.data.scheduleTypes,
    })
  );
}

export const fetchSchedule = (params?: {
  date?: string,
  program?: number,
  scheduleType?: number,
}) => (dispatch: any, getState: any) => {
  params = params || {};
  const scheduleState = readSchedule(getState);
  const gated = !!scheduleState.intendedBoard;
  const callerPassedType = params.scheduleType;
  let date = params.date;
  let scheduleType = params.scheduleType;
  let program = params.program;

  if (gated) {
    const board = scheduleState.intendedBoard;
    if (!date) {
      date = board.date;
    }
    if (positiveId(program) == null) {
      program = board.programId;
    }
    if (positiveId(scheduleType) == null) {
      scheduleType = board.scheduleTypeId;
    }
  } else {
    if (!scheduleType) {
      scheduleType = scheduleState.type.id;
    }
    if (!program) {
      program = scheduleState.program;
    }
    if (!date) {
      date = scheduleState.date;
    }
  }

  const captured = gated
    ? {
        epoch: scheduleState.boardEpoch,
        date,
        programId: positiveId(program),
        scheduleTypeId: positiveId(scheduleType),
      }
    : null;

  const run = async () => {
    try {
      // One render: the selected type and the loading flag. A separate
      // type update left the coach roster on screen under the new tab.
      batch(() => {
        if (callerPassedType) {
          dispatch(
            scheduleSliceActions.patchMeta({
              type: {
                id: scheduleType,
                type: scheduleState.type.type || '',
              },
            })
          );
        }
        dispatch(scheduleSliceActions.getStart());
      });

      const source = axios.CancelToken.source();
      activeScheduleCancel = source;
      const schedule = await axios.post(
        `${process.env.REACT_APP_SERVER_URL || ''}/schedules/schedule_by_date/`,
        { schedule_type_id: scheduleType, date: date, program: program },
        { cancelToken: source.token }
      );

      if (!schedule.data) {
        if (shouldApplyBoardResult(readSchedule(getState), captured)) {
          dispatch(
            scheduleSliceActions.getFailure({
              message: 'Empty schedule response',
            })
          );
        }
        return;
      }
      applyScheduleBody(dispatch, getState, schedule, captured, date);
    } catch (err) {
      if (axios.isCancel && axios.isCancel(err)) {
        return;
      }
      if (!shouldApplyBoardResult(readSchedule(getState), captured)) {
        return;
      }
      dispatch(scheduleSliceActions.getFailure(failurePayload(err, program)));
    }
  };

  if (!gated) {
    return run();
  }
  const key = boardFlightKey(
    captured.epoch,
    captured.programId,
    captured.date,
    captured.scheduleTypeId
  );
  return singleFlight(key, run);
};

export const setScheduleType = (scheduleType: number) => (
  dispatch: any,
  getState: any
) => {
  localStorage.setItem('scheduleType', scheduleType.toString());
  const scheduleState = readSchedule(getState);
  if (!scheduleState.intendedBoard) {
    dispatch(fetchSchedule({ scheduleType: scheduleType }));
    return;
  }
  const date = scheduleState.intendedBoard.date;
  dispatch(
    beginBoard({
      programId: scheduleState.intendedBoard.programId,
      date,
      scheduleTypeId: scheduleType,
      isMaster: !!scheduleState.intendedBoard.isMaster,
    })
  );
  dispatch(
    fetchSchedule({
      scheduleType: scheduleType,
      date,
      program: scheduleState.intendedBoard.programId,
    })
  );
};

export const getScheduleByDate = async (
  date: String,
  program: number,
  scheduleType: number
) => {
  const schedule = await axios.post(
    `${process.env.REACT_APP_SERVER_URL || ''}/schedules/schedule_by_date/`,
    { schedule_type_id: scheduleType, date: date, program: program }
  );
  return schedule;
};

export const getAllSchedulesByDate = async (date: String, userType: String) => {
  const schedules = await axios.get(
    `${
      process.env.REACT_APP_SERVER_URL || ''
    }/aerostar/reports/all_schedules_by_date?date=${date}&userType=${userType}`,
    { date: date, userType: userType }
  );
  return schedules;
};
